import { addPoses, type Pose } from "./pose.js";

/**
 * Camada procedural de voo: inclinação nas curvas, arfagem ao subir/descer e balanço das pernas.
 * Recebe o estado do voo e devolve rotações para a raiz (vassoura + atleta) e um peso para o clipe `curva`.
 */
export interface FlightState {
  /** Velocidade de giro (rad/s, positivo = curva para a esquerda). */
  turnRate: number;
  /** Velocidade vertical (m/s). */
  climb: number;
  /** Velocidade horizontal (m/s). */
  speed: number;
  /** Aceleração na direção da vassoura (m/s²): positiva acelerando, negativa freando. */
  accel?: number;
}

export interface FlightLayer {
  /** Rolagem da raiz em radianos (positivo = inclina para a esquerda). */
  roll: number;
  /** Arfagem da raiz em radianos (positivo = nariz para cima). */
  pitch: number;
  /** Peso do clipe `curva` (-1 direita … 1 esquerda). */
  lean: number;
  /** Peso da inclinação de arranque (1, corpo para a frente) ou de freada (-1, corpo para trás). */
  surge: number;
}

/** Corpo para a frente e pernas para trás ao acelerar. */
export const SURGE_FORWARD: Pose = {
  spine_01: [10, 0, 0],
  spine_02: [6, 0, 0],
  neck_01: [-8, 0, 0],
  Head: [-6, 0, 0],
  thigh_l: [8, 0, 0],
  thigh_r: [8, 0, 0],
};

/** Corpo para trás e pernas à frente ao frear. */
export const SURGE_BACK: Pose = {
  spine_01: [-12, 0, 0],
  spine_02: [-8, 0, 0],
  neck_01: [8, 0, 0],
  Head: [6, 0, 0],
  thigh_l: [-12, 0, 0],
  thigh_r: [-12, 0, 0],
  calf_l: [-14, 0, 0],
  calf_r: [-14, 0, 0],
};

/** Camada de arranque ou freada para somar à pose (peso de -1 a 1). */
export function surgeLayer(surge: number): Pose {
  return surge >= 0 ? addPoses({}, SURGE_FORWARD, surge) : addPoses({}, SURGE_BACK, -surge);
}

const MAX_ROLL = 0.75;
const MAX_PITCH = 0.35;
/** Aceleração (m/s²) que dá a inclinação máxima de arranque ou freada. */
const FULL_SURGE = 16;
const SURGE_PITCH = 0.1;

export function flightLayer(s: FlightState): FlightLayer {
  const roll = Math.max(-MAX_ROLL, Math.min(MAX_ROLL, s.turnRate * s.speed * 0.045));
  const surge = Math.max(-1, Math.min(1, (s.accel ?? 0) / FULL_SURGE));
  // Arrancando, o nariz da vassoura desce um pouco; freando, sobe.
  const climbPitch = Math.atan2(s.climb, Math.max(1, s.speed)) * 0.8;
  const pitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, climbPitch - surge * SURGE_PITCH));
  return { roll, pitch, lean: roll / MAX_ROLL, surge };
}
